/**
 * US equity-market trading calendar.
 *
 * The reconstruction engine emits a value for every calendar day, but
 * the chart should only show days the market was open — otherwise
 * weekends and holidays appear as flat ledges that dilute the visual
 * signal. Callers pass dates through {@link isTradingDay} (or
 * {@link filterTradingDays}) before handing the series to the chart.
 *
 * Scope
 * -----
 * - NYSE regular trading days only. Holiday list covers the fixed
 *   federal holidays plus Good Friday (the one non-federal market
 *   holiday). Early closes (Black Friday, Christmas Eve) are full
 *   trading days and are included.
 * - Years covered: 2023–2028 explicitly; beyond 2028 we fall back
 *   to weekend-only detection and log a console warning once so the
 *   caller knows to extend the table.
 *
 * The list is hand-maintained because computing NYSE holidays
 * programmatically (especially Good Friday, which follows the
 * ecclesiastical calendar) is more code than the table itself.
 * Sourced from nyse.com/markets/hours-calendars.
 */

/** YYYY-MM-DD strings of NYSE market closures. Early closes excluded
 *  (those are still trading days). */
const NYSE_HOLIDAYS: ReadonlySet<string> = new Set([
  // 2023
  "2023-01-02", // New Year observed
  "2023-01-16", // MLK
  "2023-02-20", // Presidents Day
  "2023-04-07", // Good Friday
  "2023-05-29", // Memorial Day
  "2023-06-19", // Juneteenth
  "2023-07-04", // Independence Day
  "2023-09-04", // Labor Day
  "2023-11-23", // Thanksgiving
  "2023-12-25", // Christmas
  // 2024
  "2024-01-01",
  "2024-01-15",
  "2024-02-19",
  "2024-03-29", // Good Friday
  "2024-05-27",
  "2024-06-19",
  "2024-07-04",
  "2024-09-02",
  "2024-11-28",
  "2024-12-25",
  // 2025
  "2025-01-01",
  "2025-01-09", // National day of mourning (President Carter)
  "2025-01-20", // MLK
  "2025-02-17",
  "2025-04-18", // Good Friday
  "2025-05-26",
  "2025-06-19",
  "2025-07-04",
  "2025-09-01",
  "2025-11-27",
  "2025-12-25",
  // 2026
  "2026-01-01",
  "2026-01-19",
  "2026-02-16",
  "2026-04-03", // Good Friday
  "2026-05-25",
  "2026-06-19",
  "2026-07-03", // Independence Day observed (Jul 4 is Sat)
  "2026-09-07",
  "2026-11-26",
  "2026-12-25",
  // 2027
  "2027-01-01",
  "2027-01-18",
  "2027-02-15",
  "2027-03-26", // Good Friday
  "2027-05-31",
  "2027-06-18", // Juneteenth observed (Jun 19 is Sat)
  "2027-07-05", // Independence Day observed (Jul 4 is Sun)
  "2027-09-06",
  "2027-11-25",
  "2027-12-24", // Christmas observed (Dec 25 is Sat)
  // 2028
  "2028-01-17",
  "2028-02-21",
  "2028-04-14", // Good Friday
  "2028-05-29",
  "2028-06-19",
  "2028-07-04",
  "2028-09-04",
  "2028-11-23",
  "2028-12-25",
]);

let _warnedBeyondTable = false;
const EARLIEST_YEAR_IN_TABLE = 2023;
const LATEST_YEAR_IN_TABLE = 2028;

/** True if `dateIso` ("YYYY-MM-DD") is a regular NYSE trading day. */
export function isTradingDay(dateIso: string): boolean {
  if (typeof dateIso !== "string" || dateIso.length < 10) return false;
  const y = Number(dateIso.slice(0, 4));
  // UTC interpretation — matches the reconstruction's date arithmetic.
  const d = new Date(`${dateIso.slice(0, 10)}T00:00:00Z`);
  const dow = d.getUTCDay(); // 0 = Sunday, 6 = Saturday
  if (dow === 0 || dow === 6) return false;
  if (y < EARLIEST_YEAR_IN_TABLE || y > LATEST_YEAR_IN_TABLE) {
    if (!_warnedBeyondTable && typeof window !== "undefined") {
      _warnedBeyondTable = true;
      // eslint-disable-next-line no-console
      console.warn(
        `[trading-calendar] year ${y} is outside the hand-maintained holiday table (${EARLIEST_YEAR_IN_TABLE}–${LATEST_YEAR_IN_TABLE}); falling back to weekend-only detection. Extend src/lib/trading-calendar.ts to cover it.`
      );
    }
    return true; // weekday outside the table — best-effort trading day
  }
  return !NYSE_HOLIDAYS.has(dateIso);
}

/** Keep only entries where the paired date is a trading day. Returns
 *  a fresh array; input is not mutated. Lengths must match. */
export function filterTradingDays<T>(
  dates: readonly string[],
  values: readonly T[]
): { dates: string[]; values: T[] } {
  const n = Math.min(dates.length, values.length);
  const outDates: string[] = [];
  const outValues: T[] = [];
  for (let i = 0; i < n; i++) {
    if (isTradingDay(dates[i]!)) {
      outDates.push(dates[i]!);
      outValues.push(values[i]!);
    }
  }
  return { dates: outDates, values: outValues };
}

/** Find the next trading day on or after `dateIso`. Useful for
 *  snapping a cashflow timestamp (which can land on a weekend — e.g.
 *  an Alpaca-side adjustment posted Saturday) to the next day the
 *  chart actually renders. */
export function nextTradingDay(dateIso: string): string {
  let cur = dateIso.slice(0, 10);
  // Hard cap to avoid an infinite loop if the table is corrupted
  // somehow. 30 days covers the worst-case holiday stretch.
  for (let i = 0; i < 30; i++) {
    if (isTradingDay(cur)) return cur;
    const d = new Date(`${cur}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    cur = d.toISOString().slice(0, 10);
  }
  return cur;
}
